/**
 * Checkout address cards, the in-form address modal, and address saves.
 */

import { paymentRequiresAddress, placeBillingPersonFields, placeShippingRecipientBlock, syncBillingPersonRequired, syncShippingRecipientRequired } from './checkout-fields.js';
import { syncShippingMethodInput, triggerCheckoutUpdate } from './checkout-delivery.js';
import { focusWithoutScroll, lockBodyScroll, unlockBodyScroll } from './modal-scroll-lock.js';

const CONTACT_IDS = ['dc_contact_first_name', 'dc_contact_last_name', 'billing_email', 'billing_phone'];

const FIELD_IDS = {
	shipping: [
		'shipping_first_name',
		'shipping_last_name',
		'shipping_company',
		'shipping_street_number',
		'shipping_address_1',
		'shipping_address_2',
		'shipping_city',
		'shipping_state',
		'shipping_postcode',
		'shipping_country',
		'shipping_phone',
	],
	billing: [
		'billing_first_name',
		'billing_last_name',
		'billing_street_number',
		'billing_address_1',
		'billing_address_2',
		'billing_city',
		'billing_state',
		'billing_postcode',
		'billing_country',
		'billing_company',
		'billing_abn',
	],
};

function i18n() {
	return window.doneCommerce?.i18n || {};
}

function checkoutForm() {
	return document.querySelector('form.checkout');
}

function previews() {
	return document.querySelector('[data-address-previews]');
}

function dialog() {
	return document.querySelector('[data-address-modal]');
}

function method() {
	const value = document.querySelector('[data-dc-checkout]')?.dataset.deliveryMethod || '';
	return value === 'collection' || value === 'delivery' ? value : '';
}

function val(id) {
	return String(document.getElementById(id)?.value || '').trim();
}

function flagOn(name) {
	return checkoutForm()?.querySelector(`input[name="${name}"]`)?.value === '1';
}

function setFlag(name, on) {
	const input = checkoutForm()?.querySelector(`input[name="${name}"]`);
	if (input) {
		input.value = on ? '1' : '0';
	}
	const root = previews();
	if (!root) {
		return;
	}
	if (name === 'dc_shipping_saved') {
		root.dataset.shippingSaved = on ? '1' : '0';
	}
	if (name === 'dc_billing_custom') {
		root.dataset.billingCustom = on ? '1' : '0';
	}
}

function shippingSaved() {
	return flagOn('dc_shipping_saved');
}

function billingCustom() {
	return flagOn('dc_billing_custom');
}

function businessOn() {
	return Boolean(document.querySelector('[data-billing-business]')?.checked);
}

function recipientBox() {
	return document.querySelector('[data-shipping-recipient]');
}

function recipientOn() {
	return Boolean(recipientBox()?.checked);
}

function norm(value) {
	return String(value || '').trim().toLowerCase();
}

function shippingForSomeoneElse() {
	if (val('shipping_company')) {
		return true;
	}
	if (!val('shipping_first_name') && !val('shipping_last_name')) {
		return false;
	}
	return norm(val('shipping_first_name')) !== norm(val('dc_contact_first_name')) || norm(val('shipping_last_name')) !== norm(val('dc_contact_last_name'));
}

function setField(id, value) {
	const input = document.getElementById(id);
	if (!input || input.value === value) {
		return;
	}
	input.value = value;
	input.dispatchEvent(new Event('input', { bubbles: true }));
	input.dispatchEvent(new Event('change', { bubbles: true }));
}

function applyContactToDelivery() {
	setField('shipping_first_name', val('dc_contact_first_name'));
	setField('shipping_last_name', val('dc_contact_last_name'));
	setField('shipping_company', '');
	if (!val('shipping_phone') && val('billing_phone')) {
		setField('shipping_phone', val('billing_phone'));
	}
}

function clearCopiedDeliveryPhone() {
	const phone = val('shipping_phone');
	if (phone && phone === val('billing_phone')) {
		setField('shipping_phone', '');
	}
}

function clearRecipientNamesIfContact() {
	['first_name', 'last_name'].forEach((key) => {
		if (norm(val(`shipping_${key}`)) === norm(val(`dc_contact_${key}`))) {
			setField(`shipping_${key}`, '');
		}
	});
}

function syncShippingRecipientToggle() {
	const box = recipientBox();
	if (!box) {
		return;
	}
	const draft = !shippingSaved() && shippingForSomeoneElse();
	box.checked = flagOn('dc_shipping_recipient') || draft;
	if (!box.checked) {
		applyContactToDelivery();
	}
	dialog()?.classList.toggle('is-shipping-recipient', box.checked);
	syncShippingRecipientRequired();
}

function deliveryLeadLines() {
	if (flagOn('dc_shipping_recipient')) {
		const lines = [];
		const company = val('shipping_company');
		if (company) {
			lines.push(company);
		}
		const name = [val('shipping_first_name'), val('shipping_last_name')].filter(Boolean).join(' ');
		if (name) {
			lines.push(name);
		}
		return lines;
	}
	const name = contactName();
	return name ? [name] : [];
}

function contactName() {
	return [val('dc_contact_first_name'), val('dc_contact_last_name')].filter(Boolean).join(' ');
}

function contactComplete() {
	if (!val('dc_contact_first_name') || !val('dc_contact_last_name') || !val('billing_phone')) {
		return false;
	}
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val('billing_email'));
}

function addressComplete(prefix) {
	return Boolean(val(`${prefix}_address_1`) && val(`${prefix}_city`) && val(`${prefix}_state`) && val(`${prefix}_postcode`));
}

function countryName(prefix) {
	const code = val(`${prefix}_country`).toUpperCase();
	if (!code || code === 'AU') {
		return i18n().australia || 'Australia';
	}
	const select = document.getElementById(`${prefix}_country`);
	const label = select?.selectedOptions?.[0]?.textContent?.trim();
	return label || code;
}

function fullName(prefix) {
	let first = val(`${prefix}_first_name`);
	let last = val(`${prefix}_last_name`);
	if (prefix === 'shipping') {
		first = first || val('dc_contact_first_name');
		last = last || val('dc_contact_last_name');
	}
	return [first, last].filter(Boolean).join(' ');
}

function addressLines(prefix) {
	const streetLine = [val(`${prefix}_street_number`), val(`${prefix}_address_1`)].filter(Boolean).join(' ');
	const street = [streetLine, val(`${prefix}_address_2`)].filter(Boolean).join(', ');
	const region = [val(`${prefix}_state`), val(`${prefix}_postcode`)].filter(Boolean).join(' ');
	const locality = [val(`${prefix}_city`), region].filter(Boolean).join(' ');
	const lines = [];
	if (street) {
		lines.push(street);
	}
	if (locality) {
		lines.push(locality);
	}
	if (street || locality) {
		lines.push(countryName(prefix));
	}
	return lines;
}

function contactLines() {
	if (billingCustom() && businessOn()) {
		return [val('billing_company')].filter(Boolean);
	}
	if (billingCustom()) {
		return [fullName('billing')].filter(Boolean);
	}
	return [contactName(), val('billing_email'), val('billing_phone')].filter(Boolean);
}

function renderLines(container, lines) {
	if (!container) {
		return;
	}
	container.replaceChildren(
		...lines.map((text) => {
			const line = document.createElement('p');
			line.textContent = text;
			return line;
		})
	);
}

function quietShipping() {
	const label = i18n().shippingPending || 'Pending';
	const tip = i18n().deliveryCostPending || 'Calculated when you add an address';
	const html = `<span class="dc-shipping-pending">${escapeHtml(label)} <span class="dc-tooltip" tabindex="0" aria-label="${escapeHtml(tip)}"><span class="dc-shipping-pending__tip" aria-hidden="true">?</span><span class="dc-tooltip__text" role="tooltip">${escapeHtml(tip)}</span></span></span>`;
	document.querySelectorAll('#order_review tr.shipping td, .woocommerce-checkout-review-order-table tr.shipping td').forEach((cell) => {
		cell.dataset.dcQuiet = '1';
		if (cell.querySelector('.dc-shipping-pending')) {
			return;
		}
		cell.innerHTML = html;
	});
}

function showNotice() {
	const root = previews();
	const notice = root?.querySelector('[data-address-gate-notice]');
	if (notice) {
		notice.hidden = false;
	}
	root?.classList.add('is-attention');
}

function hideNotice() {
	const root = previews();
	const notice = root?.querySelector('[data-address-gate-notice]');
	if (notice) {
		notice.hidden = true;
	}
	root?.classList.remove('is-attention');
}

function paintCards() {
	const root = previews();
	if (!root) {
		return;
	}

	const shipCard = root.querySelector('[data-address-card="shipping"]');
	const billCard = root.querySelector('[data-address-card="billing"]');
	const heading = root.querySelector('[data-address-heading="shipping"]');
	const edit = root.querySelector('[data-address-edit="shipping"]');
	const collect = method() === 'collection';

	if (heading) {
		heading.textContent = collect
			? i18n().collectFrom || 'Collect from'
			: i18n().weWillDeliverTo || 'We will deliver to';
	}
	if (edit) {
		edit.hidden = collect;
	}
	shipCard?.classList.toggle('is-editable', !collect);
	billCard?.classList.add('is-editable');

	if (collect) {
		const lines = window.doneCommerce?.clickCollect?.destinationLines;
		renderLines(root.querySelector('[data-address-lines="shipping"]'), Array.isArray(lines) ? lines : []);
	} else if (shippingSaved()) {
		const shipping = [...deliveryLeadLines(), ...addressLines('shipping')].filter(Boolean);
		renderLines(root.querySelector('[data-address-lines="shipping"]'), shipping);
	} else if (method() === 'delivery') {
		renderLines(root.querySelector('[data-address-lines="shipping"]'), [
			i18n().addDeliveryAddress || 'Add delivery address',
		]);
	}

	const billing = contactLines();
	if (billingCustom()) {
		billing.push(...addressLines('billing'));
	}
	renderLines(root.querySelector('[data-address-lines="billing"]'), billing);
}

function syncVisibility() {
	const root = previews();
	if (!root) {
		return;
	}

	const chosen = method() === 'collection' || method() === 'delivery';
	root.hidden = !chosen;

	const gate = method() === 'delivery' && !shippingSaved();
	root.classList.toggle('is-address-gate', gate);

	const grid = root.querySelector('[data-address-grid]');
	const shipCard = root.querySelector('[data-address-card="shipping"]');
	const billCard = root.querySelector('[data-address-card="billing"]');

	if (grid) {
		grid.hidden = false;
	}
	if (shipCard) {
		shipCard.hidden = false;
	}
	if (billCard) {
		billCard.hidden = false;
	}
	if (!gate) {
		hideNotice();
	}

	if (method() === 'delivery' && !shippingSaved()) {
		quietShipping();
	}

	paintCards();
}

let parked = [];
let snapshot = null;
let returnFocus = null;
let scrollLocked = false;
let saveToken = 0;
let saving = false;

function park(node, slot) {
	if (!node || !slot || !node.parentNode) {
		return;
	}
	const marker = document.createComment('dc-address-slot');
	node.parentNode.insertBefore(marker, node);
	parked.push({ node, marker });
	slot.appendChild(node);
}

function restoreParked() {
	for (let index = parked.length - 1; index >= 0; index -= 1) {
		const { node, marker } = parked[index];
		if (marker.parentNode) {
			marker.parentNode.insertBefore(node, marker);
			marker.remove();
		}
	}
	parked = [];
}

function muteAddressUpdates(root) {
	root?.querySelectorAll('.address-field, .update_totals_on_change').forEach((row) => {
		if (row.dataset.dcMuted === '1') {
			return;
		}
		row.dataset.dcMuted = '1';
		row.dataset.dcHadAddress = row.classList.contains('address-field') ? '1' : '0';
		row.dataset.dcHadUpdate = row.classList.contains('update_totals_on_change') ? '1' : '0';
		row.classList.remove('address-field', 'update_totals_on_change');
	});
}

function unmuteAddressUpdates(root) {
	root?.querySelectorAll('[data-dc-muted="1"]').forEach((row) => {
		if (row.dataset.dcHadAddress === '1') {
			row.classList.add('address-field');
		}
		if (row.dataset.dcHadUpdate === '1') {
			row.classList.add('update_totals_on_change');
		}
		delete row.dataset.dcMuted;
		delete row.dataset.dcHadAddress;
		delete row.dataset.dcHadUpdate;
	});
}

function readFields(which) {
	const data = {};
	(FIELD_IDS[which] || []).forEach((id) => {
		const el = document.getElementById(id);
		data[id] = el ? el.value : '';
	});
	return data;
}

function writeFields(data) {
	Object.entries(data).forEach(([id, value]) => {
		const el = document.getElementById(id);
		if (el && el.value !== value) {
			el.value = value;
		}
	});
}

function restoreRecipientIdentity(saved) {
	['shipping_first_name', 'shipping_last_name', 'shipping_company'].forEach((id) => {
		const el = document.getElementById(id);
		if (!el || !Object.prototype.hasOwnProperty.call(saved.fields || {}, id)) {
			return;
		}
		el.value = saved.fields[id];
	});

	const phone = document.getElementById('shipping_phone');
	const previousPhone = saved.fields?.shipping_phone || '';
	if (phone && !String(phone.value || '').trim() && previousPhone) {
		phone.value = previousPhone;
	}

	const box = recipientBox();
	if (box) {
		box.checked = Boolean(saved.recipient);
	}
	dialog()?.classList.toggle('is-shipping-recipient', Boolean(saved.recipient));
	syncShippingRecipientRequired();
}

function clearStreet(which) {
	const keep = which === 'shipping' ? ['shipping_country', 'shipping_phone'] : ['billing_country'];
	(FIELD_IDS[which] || []).forEach((id) => {
		if (keep.includes(id)) {
			return;
		}
		const el = document.getElementById(id);
		if (el) {
			el.value = '';
		}
	});
}

function lockScroll() {
	if (scrollLocked) {
		return;
	}
	lockBodyScroll();
	scrollLocked = true;
}

function unlockScroll() {
	if (!scrollLocked) {
		return;
	}
	unlockBodyScroll();
	scrollLocked = false;
}

function showModalError(message) {
	const error = dialog()?.querySelector('[data-address-modal-error]');
	if (!error) {
		return;
	}
	error.textContent = message;
	error.hidden = !message;
}

function setExpanded(prefix, expanded) {
	const container =
		prefix === 'billing'
			? document.querySelector('.woocommerce-billing-fields')
			: document.querySelector('.woocommerce-shipping-fields');
	container?.classList.toggle('is-address-expanded', expanded);
	container?.classList.toggle('is-address-collapsed', !expanded);
	const modal = dialog();
	if (modal && modal.dataset.addressModalFor === prefix) {
		modal.classList.toggle('is-address-expanded', expanded);
	}
	container?.querySelector('[data-address-lookup-manual]')?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
	document.querySelector(`[data-address-lookup="${prefix}"] [data-address-lookup-manual]`)?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
}

function openModal(which, opener) {
	const modal = dialog();
	const form = checkoutForm();
	if (!modal || !form || (which !== 'shipping' && which !== 'billing')) {
		return;
	}
	if (!modal.hidden) {
		closeModal({ restore: true });
	}

	returnFocus = opener || document.activeElement;
	modal.dataset.addressModalFor = which;

	const title = modal.querySelector('[data-address-modal-title]');
	if (title) {
		title.textContent =
			which === 'billing'
				? i18n().billingAddress || 'Billing address'
				: i18n().deliveryAddress || 'Delivery address';
	}

	const extra = modal.querySelector('[data-address-modal-extra]');
	const lookupSlot = modal.querySelector('[data-address-modal-lookup]');
	const fieldsSlot = modal.querySelector('[data-address-modal-fields]');

	if (which === 'shipping') {
		const shipping = document.querySelector('.woocommerce-shipping-fields');
		placeShippingRecipientBlock();
		park(shipping?.querySelector('[data-shipping-recipient-block]'), extra);
		park(shipping?.querySelector('[data-address-lookup="shipping"]'), lookupSlot);
		park(shipping?.querySelector('.shipping_address'), fieldsSlot);
	} else {
		const billing = document.querySelector('.woocommerce-billing-fields');
		placeBillingPersonFields();
		park(billing?.querySelector('.dc-billing-business'), extra);
		park(billing?.querySelector('[data-billing-person-fields]'), extra);
		park(billing?.querySelector('[data-billing-business-fields]'), extra);
		park(document.querySelector('[data-address-lookup="billing"]'), lookupSlot);
		park(billing?.querySelector('.woocommerce-billing-fields__field-wrapper'), fieldsSlot);
		const box = document.querySelector('[data-billing-business]');
		if (box && !billingCustom()) {
			box.checked = val('billing_company') !== '';
		}
		modal.classList.toggle('is-billing-business', Boolean(box?.checked));
	}

	muteAddressUpdates(modal);
	if (which === 'shipping') {
		syncShippingRecipientToggle();
		const phone = document.getElementById('shipping_phone');
		if (phone && !recipientOn() && String(phone.value || '').trim() === '' && val('billing_phone')) {
			phone.value = val('billing_phone');
		}
	}
	const draft = ['street_number', 'address_1', 'address_2', 'city', 'state', 'postcode'].some(
		(key) => val(`${which}_${key}`) !== ''
	);
	setExpanded(which, draft);

	snapshot = {
		fields: readFields(which),
		business: businessOn(),
		recipient: which === 'shipping' && recipientOn(),
	};

	showModalError('');
	modal.hidden = false;
	// Required markers need the modal visible; syncBillingPersonRequired checks hidden.
	if (which === 'billing') {
		syncBillingPersonRequired();
	}
	lockScroll();
	window.setTimeout(() => {
		const input = modal.querySelector('[data-address-lookup-input]');
		if (input) {
			focusWithoutScroll(input);
			return;
		}
		focusWithoutScroll(modal.querySelector('.dc-modal__panel'));
	}, 0);
}

function closeModal({ restore }) {
	const modal = dialog();
	if (!modal) {
		return;
	}

	const which = modal.dataset.addressModalFor;

	if (restore) {
		const gate = which === 'shipping' && !shippingSaved();
		if (which === 'billing' && !billingCustom()) {
			clearStreet('billing');
			const box = document.querySelector('[data-billing-business]');
			if (box) {
				box.checked = false;
			}
			modal.classList.remove('is-billing-business');
		} else if (!gate && snapshot) {
			writeFields(snapshot.fields);
			if (which === 'billing') {
				const box = document.querySelector('[data-billing-business]');
				if (box) {
					box.checked = snapshot.business;
				}
				modal.classList.toggle('is-billing-business', snapshot.business);
			}
			if (which === 'shipping') {
				const box = recipientBox();
				if (box) {
					box.checked = snapshot.recipient;
				}
				modal.classList.toggle('is-shipping-recipient', snapshot.recipient);
				syncShippingRecipientRequired();
			}
		} else if (gate && snapshot && which === 'shipping') {
			restoreRecipientIdentity(snapshot);
		}
	}

	unmuteAddressUpdates(modal);
	restoreParked();
	modal.hidden = true;
	modal.classList.remove('is-address-expanded', 'is-billing-business', 'is-shipping-recipient');
	delete modal.dataset.addressModalFor;
	if (which === 'shipping' || which === 'billing') {
		setExpanded(which, false);
	}
	showModalError('');
	if (which === 'shipping' && !flagOn('dc_shipping_recipient')) {
		const box = recipientBox();
		if (box?.checked && !val('shipping_first_name') && !val('shipping_last_name') && !val('shipping_company')) {
			box.checked = false;
		}
		syncShippingRecipientRequired();
	}
	syncBillingPersonRequired();
	unlockScroll();
	snapshot = null;
	if (returnFocus && typeof returnFocus.focus === 'function') {
		focusWithoutScroll(returnFocus);
	}
	returnFocus = null;
}

function setTotalsPending(on) {
	document
		.querySelectorAll(
			'#order_review .order-total, #order_review tr.shipping, .woocommerce-checkout-review-order-table .order-total, .woocommerce-checkout-review-order-table tr.shipping'
		)
		.forEach((row) => {
			row.classList.toggle('is-pending', on);
		});
}

function escapeHtml(value) {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function patchTotals(data) {
	const cell = document.querySelector('#order_review tr.shipping td, .woocommerce-checkout-review-order-table tr.shipping td');
	if (cell && (data.shipping_html || data.shipping_label)) {
		delete cell.dataset.dcQuiet;
		const label = data.shipping_label ? `${escapeHtml(data.shipping_label)} ` : '';
		cell.innerHTML = `${label}${data.shipping_html || ''}`;
	}

	const totalCell = document.querySelector('#order_review .order-total td, .woocommerce-checkout-review-order-table .order-total td');
	if (totalCell && data.order_total_html) {
		totalCell.innerHTML = data.order_total_html;
	}

	const taxTotals = document.querySelectorAll('#order_review tr.tax-total td, .woocommerce-checkout-review-order-table tr.tax-total td');
	if (taxTotals.length === 1 && data.tax_html) {
		taxTotals[0].innerHTML = data.tax_html;
	} else if (data.tax_html) {
		const rates = document.querySelectorAll('#order_review tr.tax-rate td, .woocommerce-checkout-review-order-table tr.tax-rate td');
		if (rates.length === 1) {
			rates[0].innerHTML = data.tax_html;
		}
	}

	const amount = document.querySelector('#order_review .order-total .amount, .woocommerce-checkout-review-order-table .order-total .amount');
	const mobile = document.querySelector('[data-mobile-total]');
	if (mobile && amount) {
		mobile.textContent = amount.textContent.trim();
	}

	if (data.chosen_rate_id) {
		syncShippingMethodInput(data.chosen_rate_id);
	}
}

function payloadFields(which) {
	const prefix = which;
	const recipient = which === 'shipping' && recipientOn();
	const business = which === 'billing' && businessOn();
	let firstName = '';
	let lastName = '';
	let company = '';
	if (which === 'shipping') {
		firstName = recipient ? val('shipping_first_name') : val('dc_contact_first_name');
		lastName = recipient ? val('shipping_last_name') : val('dc_contact_last_name');
		company = recipient ? val('shipping_company') : '';
	} else if (business) {
		company = val('billing_company');
	} else {
		firstName = val('billing_first_name');
		lastName = val('billing_last_name');
	}
	return {
		first_name: firstName,
		last_name: lastName,
		company,
		abn: which === 'billing' && businessOn() ? val('billing_abn') : '',
		street_number: val(`${prefix}_street_number`),
		address_1: val(`${prefix}_address_1`),
		address_2: val(`${prefix}_address_2`),
		city: val(`${prefix}_city`),
		state: val(`${prefix}_state`),
		postcode: val(`${prefix}_postcode`),
		country: val(`${prefix}_country`) || 'AU',
		phone: which === 'shipping' ? val('shipping_phone') : val('billing_phone'),
	};
}

async function saveAddress() {
	const modal = dialog();
	if (!modal || modal.hidden || saving) {
		return;
	}

	const which = modal.dataset.addressModalFor;
	if (which !== 'shipping' && which !== 'billing') {
		return;
	}

	if (!addressComplete(which)) {
		showModalError(
			which === 'shipping'
				? i18n().deliveryAddressRequired || 'A delivery address is required to deliver this order.'
				: i18n().addressIncomplete || 'Enter a street, suburb, state, and postcode.'
		);
		return;
	}

	if (which === 'shipping' && recipientOn() && (!val('shipping_first_name') || !val('shipping_last_name'))) {
		showModalError(i18n().recipientNameRequired || 'Enter the recipient’s first and last name.');
		return;
	}

	if (which === 'shipping' && !recipientOn()) {
		applyContactToDelivery();
	}

	if (which === 'billing' && businessOn() && !val('billing_company')) {
		showModalError(i18n().companyRequired || 'Company is required when billing as a business.');
		return;
	}

	if (which === 'billing' && !businessOn() && (!val('billing_first_name') || !val('billing_last_name'))) {
		showModalError(i18n().billingNameRequired || 'Enter the billing first and last name.');
		return;
	}

	if (which === 'billing' && !businessOn()) {
		['billing_company', 'billing_abn'].forEach((id) => {
			const el = document.getElementById(id);
			if (el) {
				el.value = '';
			}
		});
	}

	const button = modal.querySelector('[data-address-modal-save]');
	const token = ++saveToken;
	const methodAtSend = method();
	saving = true;
	if (button) {
		button.disabled = true;
	}
	setTotalsPending(true);
	showModalError('');

	try {
		const config = window.doneCommerce || {};
		const response = await fetch(`${config.restUrl}done-commerce/v1/checkout-address`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': config.nonce,
			},
			body: JSON.stringify({
				type: which,
				business: which === 'billing' && businessOn(),
				recipient: which === 'shipping' && recipientOn(),
				fields: payloadFields(which),
			}),
		});
		let data = {};
		try {
			data = await response.json();
		} catch {
			data = {};
		}

		if (token !== saveToken) {
			return;
		}
		if (which === 'shipping' && method() !== methodAtSend) {
			return;
		}
		if (!response.ok) {
			showModalError(data.message || i18n().addressSaveFailed || 'We couldn’t save that address. Please try again.');
			return;
		}

		if (which === 'shipping') {
			setFlag('dc_shipping_saved', true);
			setFlag('dc_shipping_recipient', recipientOn());
		} else {
			setFlag('dc_billing_custom', true);
		}
		patchTotals(data);
		closeModal({ restore: false });
		syncVisibility();
		triggerCheckoutUpdate();
	} catch {
		if (token === saveToken) {
			showModalError(i18n().addressSaveFailed || 'We couldn’t save that address. Please try again.');
		}
	} finally {
		saving = false;
		setTotalsPending(false);
		if (button) {
			button.disabled = false;
		}
	}
}

function onPlaceOrder(event) {
	const modal = dialog();
	if (modal && !modal.hidden) {
		event.preventDefault();
		event.stopImmediatePropagation();
		showModalError(i18n().saveAddressToContinue || 'Save this address to continue.');
		return;
	}

	if (businessOn()) {
		setFlag('dc_billing_custom', true);
	}

	if (!contactComplete()) {
		return;
	}

	if (method() === 'delivery' && !shippingSaved()) {
		event.preventDefault();
		event.stopImmediatePropagation();
		syncVisibility();
		showNotice();
		const card = previews()?.querySelector('[data-address-card="shipping"]') || previews();
		card?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		return;
	}

	if (paymentRequiresAddress() && !billingCustom() && (method() === 'collection' || shippingSaved())) {
		event.preventDefault();
		event.stopImmediatePropagation();
		openModal('billing', document.querySelector('[data-address-edit="billing"]'));
	}
}

function onCheckoutError() {
	if (method() === 'delivery' && !shippingSaved()) {
		if (contactComplete()) {
			syncVisibility();
			showNotice();
		}
		return;
	}

	const text = document.querySelector('.woocommerce-NoticeGroup-checkout, .woocommerce-error')?.textContent || '';
	if (/company|billing/i.test(text) && (method() !== 'delivery' || shippingSaved())) {
		openModal('billing', document.querySelector('[data-address-edit="billing"]'));
		return;
	}
	if (method() === 'delivery' && /shipping|address|recipient/i.test(text)) {
		openModal('shipping', document.querySelector('[data-address-edit="shipping"]'));
	}
}

export function initCheckoutAddressCards() {
	const form = checkoutForm();
	const root = previews();
	const modal = dialog();
	if (!form || !root || !modal || form.dataset.dcAddressCards === 'true') {
		return;
	}
	form.dataset.dcAddressCards = 'true';

	root.addEventListener('click', (event) => {
		const edit = event.target.closest('[data-address-edit]');
		if (edit) {
			event.preventDefault();
			const which = edit.getAttribute('data-address-edit');
			if (which === 'shipping' && method() === 'collection') {
				return;
			}
			openModal(which, edit);
			return;
		}
		const card = event.target.closest('[data-address-card]');
		if (!card?.classList.contains('is-editable')) {
			return;
		}
		openModal(card.getAttribute('data-address-card'), card);
	});

	modal.addEventListener('click', (event) => {
		if (event.target.closest('[data-address-modal-save]')) {
			event.preventDefault();
			saveAddress();
			return;
		}
		if (event.target.closest('[data-address-modal-cancel]')) {
			event.preventDefault();
			closeModal({ restore: true });
		}
	});

	modal.addEventListener('keydown', (event) => {
		if (event.key === 'Enter' && event.target?.tagName === 'INPUT') {
			event.preventDefault();
		}
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && modal && !modal.hidden) {
			event.preventDefault();
			closeModal({ restore: true });
		}
	});

	document.addEventListener('change', (event) => {
		if (event.target?.matches?.('[data-billing-business]')) {
			if (modal.hidden || modal.dataset.addressModalFor !== 'billing') {
				return;
			}
			modal.classList.toggle('is-billing-business', event.target.checked);
			syncBillingPersonRequired();
			return;
		}
		if (!event.target?.matches?.('[data-shipping-recipient]')) {
			return;
		}
		if (modal.hidden || modal.dataset.addressModalFor !== 'shipping') {
			return;
		}
		const on = event.target.checked;
		modal.classList.toggle('is-shipping-recipient', on);
		if (on) {
			clearCopiedDeliveryPhone();
			clearRecipientNamesIfContact();
		} else {
			applyContactToDelivery();
		}
		syncShippingRecipientRequired();
		if (on) {
			const first = document.getElementById('shipping_first_name');
			if (first) {
				focusWithoutScroll(first);
			}
		}
	});

	const onContact = (event) => {
		if (!CONTACT_IDS.includes(event.target?.id)) {
			return;
		}
		syncVisibility();
	};
	form.addEventListener('input', onContact);
	form.addEventListener('change', onContact);

	form.addEventListener('submit', onPlaceOrder, true);

	document.addEventListener('dc:delivery-method', () => {
		saveToken += 1;
		if (!modal.hidden) {
			closeModal({ restore: true });
		}
		hideNotice();
		syncVisibility();
	});

	document.addEventListener('dc:quiet-delivery-cost', () => {
		if (method() === 'delivery' && !shippingSaved()) {
			quietShipping();
		}
	});

	document.addEventListener('dc:address-expanded', (event) => {
		const prefix = event.detail?.prefix;
		if (prefix && !modal.hidden && modal.dataset.addressModalFor === prefix) {
			modal.classList.add('is-address-expanded');
		}
	});
	document.addEventListener('dc:address-collapsed', (event) => {
		const prefix = event.detail?.prefix;
		if (prefix && !modal.hidden && modal.dataset.addressModalFor === prefix) {
			modal.classList.remove('is-address-expanded');
		}
	});

	const onUpdated = () => {
		window.requestAnimationFrame(() => {
			syncVisibility();
		});
	};
	document.body.addEventListener('updated_checkout', onUpdated);
	if (typeof window.jQuery !== 'undefined') {
		window.jQuery(document.body).on('updated_checkout', onUpdated);
		window.jQuery(document.body).on('checkout_error', () => {
			window.requestAnimationFrame(() => onCheckoutError());
		});
	}

	syncVisibility();
}
